/**
 * /2fa-verify — TOTP challenge during the login flow.
 *
 * Reached when the login (or activate) response had `totp_required:true`:
 * the access cookie was issued with totp=false, so any TOTP-gated route
 * (POSH ICC, complaints, offboarding /close, etc.) would 403 with
 * `totp_required`. POSTing /auth/totp/verify with the 6-digit
 * authenticator code makes the backend re-issue wr_access with the
 * totp=true claim; we then re-hydrate /me and continue to ?next= or
 * /dashboard.
 *
 * Migration note: 1:1 port of
 * `frontend/src/app/(auth)/2fa-verify/page.tsx`.
 */

import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, ShieldCheck } from "lucide-react";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { authApi } from "@/lib/api";
import { useAuth, safeNext } from "@/lib/auth";
import { postAuthRedirect } from "@/lib/auth/safe-next";
import { totpCodeSchema, type TotpCodeValues } from "@/lib/auth/schemas";
import { handleAuthError } from "@/lib/auth/form-errors";

const twoFaVerifySearchSchema = z.object({
  next: z.string().optional(),
});

export const Route = createFileRoute("/_auth/2fa-verify")({
  validateSearch: twoFaVerifySearchSchema,
  component: TwoFactorVerifyPage,
});

function TwoFactorVerifyPage() {
  const navigate = useNavigate();
  const { next: nextRaw } = Route.useSearch();
  const { refresh, logout } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  const nextPath = safeNext(nextRaw);

  const form = useForm<TotpCodeValues>({
    resolver: zodResolver(totpCodeSchema),
    defaultValues: { code: "" },
  });

  async function onSubmit(values: TotpCodeValues) {
    setSubmitting(true);
    try {
      // Login already issued wr_access (totp=false) + wr_csrf. This
      // call re-issues wr_access with totp=true so TOTP-gated routes
      // pass.
      await authApi.totpVerify({ code: values.code });
      const me = await refresh();
      const target = postAuthRedirect(nextPath, me?.tenant?.slug);
      void navigate({ to: target, replace: true });
    } catch (err) {
      handleAuthError(err, form.setError, ["code"]);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="mb-1 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <CardTitle className="text-2xl">Two-factor verification</CardTitle>
        </div>
        <CardDescription>
          Enter the 6-digit code from your authenticator app to finish
          signing in. Two-factor is set once for your whole account.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    Authenticator code
                  </FormLabel>
                  <FormControl>
                    <Input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      maxLength={6}
                      autoFocus
                      className="text-center text-lg tracking-[0.5em]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Verify and continue
            </Button>
          </form>
        </Form>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          <button
            type="button"
            onClick={async () => {
              await logout();
              void navigate({ to: "/login", replace: true });
            }}
            className="font-medium text-primary hover:underline"
          >
            Sign in as a different user
          </button>
        </p>
      </CardContent>
    </Card>
  );
}
